import { useUpdateProp } from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import HtmlAttrId from 'client/components/core/Sidebar/components/Settings/options/HtmlAttrId'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { useWebsiteLogin } from 'client/store'
import { TFunction } from 'i18next'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { changeWebsiteLoginMode } from 'src/editor/store/website-login/website-login-actions'
import { selectors } from 'src/editor/store/website-login/website-login-reducer'
import { WebsiteLoginModeEnum } from 'src/editor/store/website-login/website-login-types'
import { WebsiteLoginInterface } from 'src/shared/types/entities/website-login-interface'
import { SelectNew } from '../ui-lib/select-new/select-new'
import { SizeAndPositionGroup } from './groups/size-and-position'

const getAccessModesChoices = (t: TFunction) => [
  {
    value: WebsiteLoginModeEnum.Login,
    label: t('entity_settings.member_access.modes.choices.login'),
  },
  {
    value: WebsiteLoginModeEnum.TwoFactorAuth,
    label: t('entities.website_login.2fa.default_headline'),
  },
  {
    value: WebsiteLoginModeEnum.Registration,
    label: t('entities.member_access.registration.default_headline'),
  },
  {
    value: WebsiteLoginModeEnum.ConfirmRegistration,
    label: t('entities.member_access.confirm_registration.default_headline'),
  },
  {
    value: WebsiteLoginModeEnum.Request,
    label: t('entity_settings.member_access.modes.choices.resetting'),
  },
  {
    value: WebsiteLoginModeEnum.Reset,
    label: t('entity_settings.member_access.modes.choices.resetting_reset'),
  },
]

export interface WebsiteLoginSettingsProps {
  entity: WebsiteLoginInterface
  defaultEntity?: WebsiteLoginInterface
}

export const WebsiteLoginSettings = ({
  entity,
  defaultEntity,
}: WebsiteLoginSettingsProps) => {
  const dispatch = useDispatch()
  const updateProp = useUpdateProp(entity)
  const { isMobile } = useDeviceModeContext()
  const mode = useWebsiteLogin(selectors.getWebsiteLoginMode)
  const { t } = useTranslation()

  const options = getAccessModesChoices(t)

  const selectedOption = useMemo(() => {
    return options.find(option => option.value === mode) || null
  }, [options, mode])

  return (
    <>
      <SelectNew
        onChange={mode =>
          dispatch(changeWebsiteLoginMode(mode?.value as WebsiteLoginModeEnum))
        }
        options={options}
        labelText="entity_settings.member_access.modes.label"
        selectedOption={selectedOption}
      />
      <SizeAndPositionGroup
        margin={isMobile ? entity.mobileMargin : entity.margin}
        defaultMargin={
          isMobile ? defaultEntity?.mobileMargin : defaultEntity?.margin
        }
        updateMargin={updateProp(isMobile ? 'mobileMargin' : 'margin')}
      />
      <HtmlAttrId attrId={entity.htmlAttrId} />
    </>
  )
}
